import {
	Card,
	Divider,
	Pagination,
	Button,
	Modal,
	Form,
	Input,
	Select,
	message,
} from 'antd';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
// import moment from 'moment';
import SearchFilter from '../../component/Filter';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getCurrencies } from '../../methods/redux/reducers/currency';
import {
	retrieveLocalSwapRates,
	createLocalSwapRate,
	updateCngnWkcSwapRate,
} from '../../methods/redux/reducers/swap';
import LocalSwapRateItem from './LocalSwapRateItem';
import NativeNumber from '../../component/Numeral';
import remakeQueryString from '../../methods/utils/remake-query-string';

export const Style = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;
`;
export const MainSection = styled(Card)`
	border-radius: 10px;
	gap: 2em;
`;

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;

const LocalSwapRates = () => {
	const dispatch = useAppDispatch();
	const [form] = Form.useForm();

	const [loading, setLoading] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [refresh, setRefresh] = useState(0);

	const [searchParams, setSearchParams] = useSearchParams();
	const { currencies } = useAppSelector((store) => store.currencies);
	const { localSwapRates } = useAppSelector((store) => store.swap);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(localSwapRates.limit),
			});
		}

		dispatch(getCurrencies({}));
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveLocalSwapRates({ ...queryData }));
		} else {
			dispatch(retrieveLocalSwapRates(queryData));
		}
	}, [searchParams, refresh]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item._id,
	}));

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
	};

	const handleSubmit = async (values: any) => {
		try {
			setLoading(true);

			const jobData = {
				baseCurrencyId: values.baseCurrencyId,
				quoteCurrencyId: values.quoteCurrencyId,
				rate: values.rate,
				volume: values.volume,
			};

			await dispatch(createLocalSwapRate(jobData));
			handleCancel();
			form.resetFields();
			setRefresh(Math.random());
		} catch (error: any) {
			message.error(error?.response?.message);
		} finally {
			setLoading(false);
		}
	};

	const handleUpdate = async () => {
		try {
			setLoading(true);

			await dispatch(updateCngnWkcSwapRate());
			setRefresh(Math.random());
		} catch (error: any) {
			message.error(error?.response?.message);
		} finally {
			setLoading(false);
		}
	};
	return (
		<Card>
			<h1>Local Swap Rates</h1>
			<div style={{ background: '#f8f8ff', padding: '2px 6px' }}>
				<p>
					When user swap for example 100USDT, we remove fee and tax of 2% before
					using the leftover to get the rate. E.g if our fee is $1, we send
					97USDT to get rates
				</p>
			</div>

			<div style={{ display: 'flex', gap: '8px' }}>
				<Button
					type="primary"
					onClick={showModal}
					style={{ marginTop: '16px', marginBottom: '16px' }}
				>
					Create Swap Rate
				</Button>
				<Button
					type="primary"
					onClick={() => handleUpdate()}
					style={{ marginTop: '16px', marginBottom: '16px' }}
				>
					Update CNGN/WKC Swap Rate
				</Button>
			</div>

			<br />
			<Divider />

			<SearchFilter
				title="Search Local Swap Rates"
				searchFilters={[
					{
						label: 'Base Currency',
						name: 'baseCurrencyId',
						htmlElement: 'select',
						selectOptions: currencySelect,
					},
					{
						label: 'Quote Currency',
						name: 'quoteCurrencyId',
						htmlElement: 'select',
						selectOptions: currencySelect,
					},
					{
						label: 'Sort',
						name: 'sort',
						htmlElement: 'select',
						selectOptions: [
							{ name: 'asc', value: 'asc' },
							{ name: 'desc', value: 'desc' },
						],
					},
				]}
				retrieveQueryString={updateQueryString}
			/>

			<br />

			<p>
				Total of{' '}
				<strong>
					<NativeNumber value={localSwapRates?.totalDocs} /> items
				</strong>{' '}
				found
			</p>

			<br />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={localSwapRates.totalDocs}
				current={localSwapRates.page}
				pageSize={localSwapRates.limit}
			/>

			<br />

			{localSwapRates.docs.map((item: any) => (
				<LocalSwapRateItem
					key={item?._id}
					data={item}
					setRefresh={setRefresh}
					currencySelect={currencySelect}
				/>
			))}

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={localSwapRates.totalDocs}
				current={localSwapRates.page}
				pageSize={localSwapRates.limit}
				showSizeChanger
				showQuickJumper
			/>

			{/* Modal for Creating a Swap Rate */}
			<Modal
				title="Create Swap Rate"
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<StyledFormItem
						name="baseCurrencyId"
						label="Base Currency"
						rules={[
							{ required: true, message: 'Please input the base currency!' },
						]}
					>
						<Select style={{ width: '100%' }} placeholder="Select a currency">
							{currencies?.map((currency: any) => (
								<Select.Option value={currency._id} key={currency._id}>
									{currency.symbol}
								</Select.Option>
							))}
						</Select>
					</StyledFormItem>

					<StyledFormItem
						name="quoteCurrencyId"
						label="Quote Currency"
						rules={[
							{ required: true, message: 'Please input the quote currency!' },
						]}
					>
						<Select style={{ width: '100%' }} placeholder="Select a currency">
							{currencies?.length > 0 &&
								currencies?.map((currency: any) => (
									<Select.Option value={currency._id} key={currency._id}>
										{currency.symbol}
									</Select.Option>
								))}
						</Select>
					</StyledFormItem>

					<StyledFormItem
						name="rate"
						label="Rate (Amount of Quote that equal to 1 Base Currency)"
						rules={[{ required: true, message: 'Please input the rate!' }]}
					>
						<Input placeholder="e.g., 1.25" />
					</StyledFormItem>

					<StyledFormItem
						name="volume"
						label="Volume"
						rules={[{ required: true, message: 'Please input volume!' }]}
					>
						<Input placeholder="e.g., 1000" />
					</StyledFormItem>
					<Form.Item>
						<Button type="primary" htmlType="submit" loading={loading}>
							{loading ? 'Creating...' : 'Create Swap Rate'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	);
};

export default LocalSwapRates;
